import type {
  ExecutionCockpitDailyBriefRequestDto,
  ExecutionCockpitDailyBriefResponseDto,
  ExecutionCockpitDailyBriefViewDto,
  ExecutionCockpitDecisionPackageActivateRequestDto,
  ExecutionCockpitDecisionPackageActivateResponseDto,
  ExecutionCockpitDecisionPackageForRunResponseDto,
  ExecutionCockpitDecisionPackageReviewHistoryResponseDto,
  ExecutionCockpitDecisionPackageReviewRequestDto,
  ExecutionCockpitDecisionPackageReviewResponseDto,
  ExecutionCockpitRetrospectiveResponseDto,
  ExecutionCockpitRunRequestDto,
  ExecutionCockpitRunResponseDto,
} from '@helix/lib';
import { apiFetch } from './apiFetch';

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function runExecutionCockpit(
  payload: ExecutionCockpitRunRequestDto,
): Promise<ExecutionCockpitRunResponseDto> {
  const res = await apiFetch('/v1/execution-cockpit/run', {
    method: 'POST',
    json: payload,
  });
  return handle<ExecutionCockpitRunResponseDto>(res);
}

export async function getExecutionCockpitRun(runId: string): Promise<ExecutionCockpitRunResponseDto> {
  const res = await apiFetch(`/v1/execution-cockpit/run/${encodeURIComponent(runId)}`, { cache: 'no-cache' });
  return handle<ExecutionCockpitRunResponseDto>(res);
}

export async function getExecutionCockpitDailyBrief(
  payload: ExecutionCockpitDailyBriefRequestDto,
): Promise<ExecutionCockpitDailyBriefResponseDto> {
  const res = await apiFetch('/v1/execution-cockpit/daily-brief', {
    method: 'POST',
    json: payload,
  });
  return handle<ExecutionCockpitDailyBriefResponseDto>(res);
}

export type ExecutionCockpitLatestRunDto = {
  runId: string;
  computedAt: string;
  hasBoardPack: boolean;
  configRefreshed?: boolean;
};

export type ExecutionCockpitStrategyConfigDto = {
  schemaVersion: string;
  derivedFrom: string;
  updatedAt: string;
  config: {
    stalledDaysThreshold: number;
    lookbackDaysForThroughput: number;
    initiatives: ExecutionCockpitRunRequestDto['config']['initiatives'];
  };
};

function appendScopeRootNodeId(path: string, scopeRootNodeId?: string | null): string {
  const scope = String(scopeRootNodeId ?? '').trim();
  if (!scope) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}scopeRootNodeId=${encodeURIComponent(scope)}`;
}

export async function getExecutionCockpitStrategyConfig(
  strategyId: string,
): Promise<ExecutionCockpitStrategyConfigDto | null> {
  const id = String(strategyId ?? '').trim();
  if (!id) throw new Error('Missing strategyId');
  const res = await apiFetch(`/v1/execution-cockpit/strategies/${encodeURIComponent(id)}/config`, { cache: 'no-cache' });
  const body = await handle<{ config: ExecutionCockpitStrategyConfigDto | null }>(res);
  return body.config;
}

export async function getLatestExecutionCockpitRun(
  strategyId: string,
  scopeRootNodeId?: string | null,
): Promise<ExecutionCockpitLatestRunDto | null> {
  const id = String(strategyId ?? '').trim();
  if (!id) throw new Error('Missing strategyId');
  const res = await apiFetch(
    appendScopeRootNodeId(
      `/v1/execution-cockpit/strategies/${encodeURIComponent(id)}/latest-run`,
      scopeRootNodeId,
    ),
    { cache: 'no-cache' },
  );
  const body = await handle<{ latestRun: ExecutionCockpitLatestRunDto | null }>(res);
  return body.latestRun;
}

export async function ensureLatestExecutionCockpitRun(
  strategyId: string,
  scopeRootNodeId?: string | null,
): Promise<ExecutionCockpitLatestRunDto | null> {
  const id = String(strategyId ?? '').trim();
  if (!id) throw new Error('Missing strategyId');
  const res = await apiFetch(
    appendScopeRootNodeId(
      `/v1/execution-cockpit/strategies/${encodeURIComponent(id)}/ensure-latest-run`,
      scopeRootNodeId,
    ),
    {
      method: 'POST',
      cache: 'no-cache',
    },
  );
  const body = await handle<{ latestRun: ExecutionCockpitLatestRunDto | null }>(res);
  return body.latestRun;
}

export async function getExecutionCockpitDailyBriefFromRun(params: {
  runId: string;
  view?: ExecutionCockpitDailyBriefViewDto | null;
  scopeRootNodeId?: string | null;
}): Promise<ExecutionCockpitDailyBriefResponseDto> {
  const runId = String(params.runId ?? '').trim();
  if (!runId) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/execution-cockpit/run/${encodeURIComponent(runId)}/daily-brief`, {
    method: 'POST',
    json: {
      view: params.view ?? undefined,
      scopeRootNodeId: String(params.scopeRootNodeId ?? '').trim() || undefined,
    },
  });
  return handle<ExecutionCockpitDailyBriefResponseDto>(res);
}

export async function activateExecutionCockpitDecisionPackageForRun(params: {
  runId: string;
  decisions: ExecutionCockpitDecisionPackageActivateRequestDto['decisions'];
}): Promise<ExecutionCockpitDecisionPackageActivateResponseDto> {
  const runId = String(params.runId ?? '').trim();
  if (!runId) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/execution-cockpit/run/${encodeURIComponent(runId)}/decision-package/activate`, {
    method: 'POST',
    json: { decisions: params.decisions ?? [] },
    cache: 'no-cache',
  });
  return handle<ExecutionCockpitDecisionPackageActivateResponseDto>(res);
}

export async function saveExecutionCockpitDecisionPackageDraftForRun(params: {
  runId: string;
  decisions: ExecutionCockpitDecisionPackageActivateRequestDto['decisions'];
}): Promise<ExecutionCockpitDecisionPackageForRunResponseDto> {
  const runId = String(params.runId ?? '').trim();
  if (!runId) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/execution-cockpit/run/${encodeURIComponent(runId)}/decision-package`, {
    method: 'PUT',
    json: { decisions: params.decisions ?? [] },
    cache: 'no-cache',
  });
  return handle<ExecutionCockpitDecisionPackageForRunResponseDto>(res);
}

export async function startExecutionCockpitDecisionPackageAmendmentForRun(
  runId: string,
): Promise<ExecutionCockpitDecisionPackageForRunResponseDto> {
  const id = String(runId ?? '').trim();
  if (!id) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/execution-cockpit/run/${encodeURIComponent(id)}/decision-package/amend`, {
    method: 'POST',
    cache: 'no-cache',
  });
  return handle<ExecutionCockpitDecisionPackageForRunResponseDto>(res);
}

export async function getExecutionCockpitDecisionPackageForRun(
  runId: string,
): Promise<ExecutionCockpitDecisionPackageForRunResponseDto> {
  const id = String(runId ?? '').trim();
  if (!id) throw new Error('Missing runId');
  const res = await apiFetch(`/v1/execution-cockpit/run/${encodeURIComponent(id)}/decision-package`, {
    cache: 'no-cache',
  });
  return handle<ExecutionCockpitDecisionPackageForRunResponseDto>(res);
}

export async function reviewExecutionCockpitDecisionPackageForRun(params: {
  runId: string;
  initiativeKeys?: ExecutionCockpitDecisionPackageReviewRequestDto['initiativeKeys'];
  outcome?: ExecutionCockpitDecisionPackageReviewRequestDto['outcome'];
  notes?: ExecutionCockpitDecisionPackageReviewRequestDto['notes'];
  ownerAction?: ExecutionCockpitDecisionPackageReviewRequestDto['ownerAction'];
  commitmentArtifactId?: ExecutionCockpitDecisionPackageReviewRequestDto['commitmentArtifactId'];
  counterProposal?: ExecutionCockpitDecisionPackageReviewRequestDto['counterProposal'];
}): Promise<ExecutionCockpitDecisionPackageReviewResponseDto> {
  const runId = String(params.runId ?? '').trim();
  if (!runId) throw new Error('Missing runId');
  const res = await apiFetch(
    `/v1/execution-cockpit/run/${encodeURIComponent(runId)}/decision-package/review`,
    {
      method: 'POST',
      json: {
        initiativeKeys: params.initiativeKeys ?? undefined,
        outcome: params.outcome ?? undefined,
        notes: params.notes ?? undefined,
        ownerAction: params.ownerAction ?? undefined,
        commitmentArtifactId: params.commitmentArtifactId ?? undefined,
        counterProposal: params.counterProposal ?? undefined,
      },
      cache: 'no-cache',
    }
  );
  return handle<ExecutionCockpitDecisionPackageReviewResponseDto>(res);
}

export async function getExecutionCockpitDecisionPackageReviewHistoryForRun(params: {
  runId: string;
  initiativeKey?: string | null;
  commitmentArtifactId?: string | null;
  limit?: number | null;
}): Promise<ExecutionCockpitDecisionPackageReviewHistoryResponseDto> {
  const runId = String(params.runId ?? '').trim();
  if (!runId) throw new Error('Missing runId');
  const initiativeKey = String(params.initiativeKey ?? '').trim();
  const commitmentArtifactId = String(
    params.commitmentArtifactId ?? '',
  ).trim();
  const queryParams = new URLSearchParams();
  if (initiativeKey) queryParams.set('initiativeKey', initiativeKey);
  if (commitmentArtifactId) {
    queryParams.set('commitmentArtifactId', commitmentArtifactId);
  }
  if (params.limit != null) queryParams.set('limit', String(params.limit));
  const query = queryParams.toString();
  const res = await apiFetch(
    `/v1/execution-cockpit/run/${encodeURIComponent(runId)}/decision-package/review-history${query ? `?${query}` : ''}`,
    { cache: 'no-cache' },
  );
  return handle<ExecutionCockpitDecisionPackageReviewHistoryResponseDto>(res);
}

export async function getExecutionCockpitRetrospective(params: {
  runId: string;
  previousRunId?: string | null;
}): Promise<ExecutionCockpitRetrospectiveResponseDto> {
  const runId = String(params.runId ?? '').trim();
  if (!runId) throw new Error('Missing runId');
  const previousRunId = String(params.previousRunId ?? '').trim();
  const query = previousRunId ? `?previousRunId=${encodeURIComponent(previousRunId)}` : '';
  const res = await apiFetch(`/v1/execution-cockpit/run/${encodeURIComponent(runId)}/retrospective${query}`, {
    cache: 'no-cache',
  });
  return handle<ExecutionCockpitRetrospectiveResponseDto>(res);
}
